import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider, Typography, Alert, Fade } from '@mui/material';
import { Stack } from '@mui/system';
import { GapSwitch } from './style';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';

const GapSlider = ({
  questionId,
  answer,
  setFrameworkData,
  curSection,
  fwId,
  onReRenderChartsAndRecommendations, // onReRender -> onSliderChange -> onReRenderChartsAndRecommendations
}) => {
  const [sliderDisabled, setSliderDisabled] = useState(!answer.applicable);
  const [sliderValue, setSliderValue] = useState(answer.value);
  const [sliderError, setSliderError] = useState(false);

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });

  const marks = [
    { value: 0, label: t('slider_mark_first') },
    { value: 33, label: t('slider_mark_second') },
    { value: 66, label: t('slider_mark_third') },
    { value: 100, label: t('slider_mark_fourth') },
  ];

  function valuetext(value) {
    const mark = marks.find(mark => mark.value === value);
    return mark ? mark.label : '';
  }

  const flipSwitch = () => {
    saveResponse(!sliderDisabled, sliderValue);
  };

  const sliderChange = (e, value) => {
    saveResponse(sliderDisabled, value);
  };

  const saveResponse = async (applicable, val) => {
    const obj = [
      {
        frameworkId: fwId,
        sectionId: curSection,
        id: questionId,
        applicable: !applicable,
        value: val,
      },
    ];
    let res = { status: 'fail' };
    try {
      res = await apiService.saveQuestionResponse(obj);
      if (res.data.status === 'success') {
        setSliderDisabled(applicable);
        setSliderValue(val);
        if (onReRenderChartsAndRecommendations) {
          onReRenderChartsAndRecommendations(questionId);
        }
        if (setFrameworkData) {
          setFrameworkData(prevData => ({
            ...prevData,
            [fwId]: {
              ...prevData[fwId],
              sections: {
                ...prevData[fwId]?.sections,
                [curSection]: {
                  ...prevData[fwId]?.sections?.[curSection],
                  nrOfQuestions: res.data.nrOfQuestions,
                  nrOfApplicable: res.data.nrOfQuestions - res.data.nrOfNotApplicable,
                  nrOfAnswered: res.data.nrOfAnswered,
                  answers: {
                    ...prevData[fwId]?.sections?.[curSection]?.answers,
                    [questionId]: {
                      ...prevData[fwId]?.sections?.[curSection]?.answers?.[questionId],
                      id: questionId,
                      applicable: !applicable,
                      value: val,
                      answered: true,
                    },
                  },
                },
              },
            },
          }));
        }
      }
    } catch (error) {
      console.error(error);
      setSliderError(true);
      setTimeout(() => {
        setSliderError(false);
      }, 3000);
      return false;
    }
  };

  return (
    <Stack direction='column'>
      <Stack direction='row' spacing={2} alignItems={'center'}>
        <Typography variant='body2'>{t('applicable')}</Typography>
        <GapSwitch checked={!sliderDisabled} onChange={flipSwitch} />
        <Typography variant='body2'>{t('estimated_progress')}</Typography>
        {sliderDisabled ? (
          <Typography variant='body2'>N/A</Typography>
        ) : (
          <Slider
            value={sliderValue}
            max={100}
            min={0}
            valueLabelDisplay='auto'
            step={null}
            marks={marks}
            disabled={sliderDisabled}
            onChange={sliderChange}
            valueLabelFormat={valuetext}
            sx={{
              width: '400px',
              color: ColorsV2.white,
              [`& .MuiSlider-markLabel`]: {
                color: ColorsV2.textInfo,
              },
              [`& .MuiSlider-mark`]: {
                bgcolor: ColorsV2.white,
              },
              ['& .MuiSlider-thumb']: {
                visibility: answer?.answered ? 'visible' : 'hidden',
                backgroundColor: ColorsV2.acent,
                border: '2px solid ' + ColorsV2.white,
                '&:hover': {
                  boxShadow: '0px 0px 0px 8px rgba(226, 75, 230, 0.16)',
                },
                '&.Mui-active': {
                  boxShadow: '0px 0px 0px 14px rgba(226, 75, 230, 0.16)', // Active (when dragging)
                },
              },
            }}
          />
        )}
      </Stack>
      <Fade in={sliderError}>
        <Alert
          severity='error'
          sx={{
            width: '100%',
            color: ColorsV2.white,
            backgroundColor: ColorsV2.primary,
            marginTop: '15px',
          }}
        >
          {t('error_saving_answer')}
        </Alert>
      </Fade>
    </Stack>
  );
};

export default GapSlider;
